import React, { useMemo, useState } from 'react';
import { formatCurrency } from '../../utils/currency';
import useCurrencyCode from '../../hooks/useCurrencyCode';
import useLiteProducts from './useLiteProducts';

export default function LiteProductLookup() {
  const currencyCode = useCurrencyCode();
  const { products, categories, loading, reload } = useLiteProducts();
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch =
        !query ||
        p.name?.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query) ||
        p.barcode?.toLowerCase().includes(query);
      const matchesCategory = !selectedCategoryId || p.categoryId === selectedCategoryId;
      return matchesSearch && matchesCategory;
    });
  }, [products, search, selectedCategoryId]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-300">Lookup</p>
          <h1 className="text-2xl font-semibold text-white">Product Lookup</h1>
          <p className="text-sm text-slate-400">Search price and stock quickly.</p>
        </div>
        <button
          type="button"
          onClick={reload}
          className="rounded-full border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:border-emerald-400"
        >
          Refresh
        </button>
      </header>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-3 shadow-inner shadow-black/30">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, SKU, or barcode"
          className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
        />
        <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-2">
          <CategoryPill
            label="All"
            active={selectedCategoryId === null}
            onClick={() => setSelectedCategoryId(null)}
          />
          {categories.map((cat) => (
            <CategoryPill
              key={cat.id}
              label={cat.name}
              active={selectedCategoryId === cat.id}
              onClick={() => setSelectedCategoryId(cat.id)}
            />
          ))}
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{filteredProducts.length} product{filteredProducts.length === 1 ? '' : 's'}</span>
          <span className="rounded-full border border-slate-800 bg-slate-900 px-3 py-1">
            One-column
          </span>
        </div>

        {loading && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-center text-sm text-slate-400">
            Loading products...
          </div>
        )}

        {!loading && filteredProducts.length === 0 && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-center text-sm text-slate-400">
            No products match this search.
          </div>
        )}

        <div className="grid grid-cols-1 gap-3">
          {filteredProducts.map((product) => (
            <article
              key={product.id}
              className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-sm shadow-black/20"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-base font-semibold text-white">{product.name}</p>
                  <p className="text-xs text-slate-400">
                    SKU {product.sku || 'N/A'} {product.barcode ? `- ${product.barcode}` : ''}
                  </p>
                </div>
                <p className="text-sm font-semibold text-emerald-300">
                  {formatCurrency(product.priceInclTax ?? product.price ?? 0, currencyCode)}
                </p>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm text-slate-300">
                <span>Stock {product.stock ?? 'N/A'}</span>
                <span className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs font-semibold text-slate-200">
                  Category {product.categoryName || 'Unassigned'}
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function CategoryPill({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition ${
        active
          ? 'bg-emerald-500 text-slate-900'
          : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
      }`}
    >
      {label}
    </button>
  );
}
